import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import {
  Autocomplete,
  TextField,
  CircularProgress,
  Chip,
  Avatar,
  ListItem,
  ListItemAvatar,
  ListItemText,
} from '@mui/material'
import {
  Search as SearchIcon,
  Public as PublicIcon,
} from '@mui/icons-material'
import Parse from '../../services/initializeParse'
import type { BookingInvitee } from '../../../../types/RoomBooking'
import type { CompanyUser } from '../../core/models/Company'
import { externalGuestSuggestion, normalizeEmail } from './externalGuests'
import { getDisplayName } from '../../utils/personName'
import ProfileAvatar from '../ProfileAvatar'

interface CompanyUserOption {
  userId: string
  name: string
  email: string
  /** Profile photo, when the member has one. */
  avatarUrl?: string
  /** Profile `avatar`: the pattern style, shown when there is no photo. */
  avatarVariant?: string
  /** Synthetic "Add <email> as external guest" row, never a real selection. */
  externalSuggestion?: true
}

const NO_EMAILS: ReadonlySet<string> = new Set()

interface InviteeSearchAutocompleteProps {
  value: BookingInvitee[]
  onChange: (invitees: BookingInvitee[]) => void
  companyId: string
  excludeUserId?: string
  disabled?: boolean
  /** When set, a typed email of someone outside the company is offered as an external guest. */
  onAddExternalGuest?: (email: string) => void
  /** Why a guest can't be added right now (no room, no reception, limit); shows the option disabled. */
  externalGuestDisabledReason?: string
  /** Normalized emails already invited as guests, so they aren't offered again. */
  guestEmails?: ReadonlySet<string>
  /** Normalized emails of every company member, for callers that validate guest emails. */
  onCompanyEmailsChange?: (emails: Set<string>) => void
}

export default function InviteeSearchAutocomplete({
  value,
  onChange,
  companyId,
  excludeUserId,
  disabled = false,
  onAddExternalGuest,
  externalGuestDisabledReason = '',
  guestEmails = NO_EMAILS,
  onCompanyEmailsChange,
}: InviteeSearchAutocompleteProps) {
  const [options, setOptions] = useState<CompanyUserOption[]>([])
  const [companyEmails, setCompanyEmails] = useState<Set<string>>(new Set())
  const onCompanyEmailsChangeRef = useRef(onCompanyEmailsChange)
  onCompanyEmailsChangeRef.current = onCompanyEmailsChange
  const [loading, setLoading] = useState(false)

  // Load all company users once on mount (exclude current user)
  useEffect(() => {
    if (!companyId) return
    let cancelled = false
    setLoading(true)

    // No pageSize: results are oldest-member-first, so a cap silently drops the newest members
    // from both the invitee search and the "is this guest a company member?" check.
    Parse.Cloud.run('getCompanyUsers', {
      company_id: companyId,
      is_active: true,
    })
      .then((result: { users?: Array<Pick<CompanyUser, 'user_id' | 'user' | 'profile'>> } | undefined) => {
        if (cancelled || !result?.users) return
        // Includes the current user and admins, who aren't selectable but aren't guests either.
        const emails = new Set<string>(
          result.users.map((u) => normalizeEmail(u.user?.email || '')).filter(Boolean),
        )
        setCompanyEmails(emails)
        onCompanyEmailsChangeRef.current?.(emails)
        const mapped = result.users
          .filter((u) => u.user_id !== excludeUserId && u.profile?.role !== 'admin')
          .map((u) => {
            return {
              userId: u.user_id,
              name: getDisplayName({ ...u.profile, username: u.user?.username }, u.user?.email || 'Unknown'),
              email: u.user?.email || '',
              avatarUrl: u.profile?.customAvatarUrl || undefined,
              avatarVariant: u.profile?.avatar,
            }
          })
        setOptions(mapped)
      })
      .catch((err: any) => {
        console.warn('[InviteeSearch] Failed to load users:', err)
        setOptions([])
      })
      .finally(() => !cancelled && setLoading(false))

    return () => { cancelled = true }
  }, [companyId, excludeUserId])

  const [inputValue, setInputValue] = useState('')
  const selectionMadeRef = useRef(false)

  const selectedUserIds = new Set(value.map((inv) => inv.userId))

  const handleSelect = useCallback(
    (_event: React.SyntheticEvent, selected: CompanyUserOption[]) => {
      selectionMadeRef.current = true
      const suggestion = selected.find((opt) => opt.externalSuggestion)
      if (suggestion) {
        setInputValue('')
        onAddExternalGuest?.(suggestion.email)
        return
      }
      const invitees: BookingInvitee[] = selected.map((opt) => ({
        userId: opt.userId,
        email: opt.email,
        name: opt.name,
        type: 'internal' as const,
        status: 'pending' as const,
      }))
      onChange(invitees)
    },
    [onChange, onAddExternalGuest],
  )

  const handleInputChange = useCallback(
    (_event: React.SyntheticEvent, newValue: string, reason: string) => {
      if (reason === 'input' || reason === 'clear') {
        setInputValue(newValue)
      } else if (reason === 'reset') {
        // Only clear after an actual selection, not on spurious value-prop rerenders
        if (selectionMadeRef.current) {
          setInputValue('')
          selectionMadeRef.current = false
        }
      }
    },
    [],
  )

  // Chips take the member's photo from the loaded company list: saved invitees don't carry one.
  const optionsByUserId = useMemo(() => new Map(options.map((opt) => [opt.userId, opt])), [options])

  // Memoize to keep a stable reference and avoid MUI resetting inputValue
  const selectedOptions = useMemo<CompanyUserOption[]>(
    () => value.map((inv) => {
      const member = inv.userId ? optionsByUserId.get(inv.userId) : undefined
      return {
        userId: inv.userId || '',
        name: inv.name || inv.email || '',
        email: inv.email || '',
        avatarUrl: member?.avatarUrl,
        avatarVariant: member?.avatarVariant,
      }
    }),
    [value, optionsByUserId],
  )

  return (
    <Autocomplete
      multiple
      value={selectedOptions}
      onChange={handleSelect}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      options={options}
      getOptionLabel={(opt) => `${opt.name} ${opt.email}`}
      isOptionEqualToValue={(opt, val) => opt.userId === val.userId}
      filterOptions={(opts, state) => {
        const q = state.inputValue.toLowerCase()
        const matches = opts.filter((o) =>
          !selectedUserIds.has(o.userId) &&
          (!q || o.name.toLowerCase().includes(q) || o.email.toLowerCase().includes(q))
        )
        if (!onAddExternalGuest) return matches
        const guestEmail = externalGuestSuggestion(state.inputValue, { companyEmails, takenEmails: guestEmails })
        if (!guestEmail) return matches
        return [
          ...matches,
          { userId: `external:${normalizeEmail(guestEmail)}`, name: guestEmail, email: guestEmail, externalSuggestion: true as const },
        ]
      }}
      getOptionDisabled={(opt) => !!opt.externalSuggestion && !!externalGuestDisabledReason}
      loading={loading}
      disabled={disabled}
      renderTags={(tagValue, getTagProps) =>
        tagValue.map((opt, index) => {
          const { key, ...chipProps } = getTagProps({ index })
          return (
            <Chip
              key={key}
              label={opt.name || opt.email}
              size="small"
              avatar={<ProfileAvatar userId={opt.userId} name={opt.name || opt.email} photoUrl={opt.avatarUrl} patternStyle={opt.avatarVariant} size={24} />}
              {...chipProps}
            />
          )
        })
      }
      renderOption={(props, option) => {
        const { key, ...rest } = props as any
        if (option.externalSuggestion) {
          return (
            <ListItem key={key} {...rest} sx={{ gap: 1 }}>
              <ListItemAvatar sx={{ minWidth: 40 }}>
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'var(--sevv-primary, #f97415)' }}>
                  <PublicIcon sx={{ fontSize: 18 }} />
                </Avatar>
              </ListItemAvatar>
              <ListItemText
                primary={`Add "${option.email}" as external guest`}
                secondary={externalGuestDisabledReason || 'Invite someone outside the company'}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
                secondaryTypographyProps={{ variant: 'caption', sx: { color: 'text.secondary' } }}
              />
            </ListItem>
          )
        }
        return (
          <ListItem key={key} {...rest} sx={{ gap: 1 }}>
            <ListItemAvatar sx={{ minWidth: 40 }}>
              <ProfileAvatar userId={option.userId} name={option.name} photoUrl={option.avatarUrl} patternStyle={option.avatarVariant} size={32} />
            </ListItemAvatar>
            <ListItemText
              primary={option.name}
              secondary={option.email}
              primaryTypographyProps={{ variant: 'body2', fontWeight: 500 }}
              secondaryTypographyProps={{ variant: 'caption', sx: { color: 'text.secondary' } }}
            />
          </ListItem>
        )
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label="Invitees"
          placeholder={value.length === 0 ? (onAddExternalGuest ? 'Search members or enter a guest email...' : 'Search company members...') : ''}
          InputProps={{
            ...params.InputProps,
            startAdornment: (
              <>
                <SearchIcon sx={{ color: 'text.secondary', mr: 0.5, fontSize: 20 }} />
                {params.InputProps.startAdornment}
              </>
            ),
            endAdornment: (
              <>
                {loading && <CircularProgress size={18} />}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      noOptionsText="No users found"
      loadingText="Loading..."
    />
  )
}
